import React, { useState, useEffect } from 'react';
import { Menu, Home, AlertTriangle, Tag, Plus, Search, Filter, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
const API_BASE_URL = 'https://ior.org/risk-register/web/api';



// Simple Alert Component
const Alert = ({ children, onClose }) => (
  <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 relative">
    <div className="flex items-center">
      <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
      <p className="text-red-700">{children}</p>
    </div>
    {onClose && (
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-red-400 hover:text-red-600"
      >
        ×
      </button>
    )}
  </div>
);

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <XCircle className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Category Form Component
const CategoryForm = ({ category, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    category_code: category?.category_code || '',
    label: category?.label || '',
    description: category?.description || '',
    color: category?.color || 'blue'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Category Code
        </label>
        <input
          type="text"
          value={formData.category_code}
          onChange={(e) => setFormData({ ...formData, category_code: e.target.value })}
          className="w-full p-2 border rounded-md"
          required
          disabled={!!category} // Disable for edit mode
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Label
        </label>
        <input
          type="text"
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          className="w-full p-2 border rounded-md"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full p-2 border rounded-md"
          rows={3}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Color
        </label>
        <select
          value={formData.color}
          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
          className="w-full p-2 border rounded-md"
          required
        >
          <option value="blue">Blue</option>
          <option value="green">Green</option>
          <option value="red">Red</option>
          <option value="yellow">Yellow</option>
          <option value="purple">Purple</option>
          <option value="orange">Orange</option>
          <option value="pink">Pink</option>
          <option value="indigo">Indigo</option>
        </select>
      </div>
      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-600 hover:text-gray-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {category ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

const RiskCategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const { user } = useAuth();
  const token = localStorage.getItem('authToken');  

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/risk-categories.php?search=${searchTerm}&status=${selectedFilter}`);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.data);
        setError(null);
      } else {
        setError('Failed to fetch categories');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchCategories();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, selectedFilter]);

  // Handle category submission (create/update)
  const handleCategorySubmit = async (formData) => {
    try {
      const method = editingCategory ? 'PUT' : 'POST';
      const url = editingCategory 
        ? `${API_BASE_URL}/risk-categories.php?id=${editingCategory.id}`
        : `${API_BASE_URL}/risk-categories.php`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        fetchCategories();
        setModalOpen(false);
        setEditingCategory(null);
      } else {
        setError('Failed to save category');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle edit button click
  const handleEditClick = (category) => {
    setEditingCategory(category);
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCategory(null);
  };

  // Category Badge Component
  const CategoryBadge = ({ category }) => {
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      pink: 'bg-pink-100 text-pink-800 border-pink-200',
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colorClasses[category.color]}`}>
        <Tag className="w-4 h-4 mr-1" />
        {category.label}
      </span>
    );
  };

  // Archive category
  const handleArchiveCategory = async (categoryId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/risk-categories.php?id=${categoryId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        fetchCategories();
      } else {
        setError('Failed to archive category');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Risk Categories</h2>
        <button 
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <div className="flex space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search categories..."
            className="pl-10 pr-4 py-2 w-full border rounded-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <select
            className="pl-10 pr-4 py-2 border rounded-md appearance-none bg-white"
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Category Form Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleModalClose}
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
      >
        <CategoryForm
          category={editingCategory}
          onSubmit={handleCategorySubmit}
          onClose={handleModalClose}
        />
      </Modal>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        /* Categories Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categories.map(category => (
            <div key={category.id} className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <CategoryBadge category={category} />
                <span className="text-sm text-gray-500">
                  {category.risk_count} {category.risk_count === 1 ? 'Risk' : 'Risks'}
                </span>
              </div>
              <p className="text-gray-600 mb-4">{category.description}</p>
              <div className="flex justify-end space-x-2">
                <button 
                  className="text-sm text-gray-600 hover:text-gray-900"
                  onClick={() => handleEditClick(category)}
                >
                  Edit
                </button>
                <button 
                  className="text-sm text-gray-600 hover:text-gray-900"
                  onClick={() => handleArchiveCategory(category.id)}
                >
                  Archive
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RiskCategoryManager;
